/**
 * Query key factories used with TanStack Query.
 * Keeping keys centralized helps avoid typos and eases cache invalidation.
 */
export const paymentKeys = {
  all: ["payments"] as const,
  open: (search?: string, intentionStatuses?: string[]) => {
    const parts = [...paymentKeys.all, "open"];
    if (search) {
      parts.push("search", search);
    }
    if (intentionStatuses && intentionStatuses.length > 0) {
      parts.push("intention-statuses", ...intentionStatuses);
    }
    return parts as readonly string[];
  },
  archive: () => [...paymentKeys.all, "archive"] as const,
  paidArchiveDashboard: (params: Record<string, unknown>) =>
    [...paymentKeys.all, "paid-archive-dashboard", params] as const,
  detail: (externalId: string) =>
    [...paymentKeys.all, "detail", externalId] as const,
  history: (externalId: string) =>
    [...paymentKeys.detail(externalId), "history"] as const,
  lastBucketStatus: (externalId: string) =>
    [...paymentKeys.detail(externalId), "last-bucket-status"] as const,
  emailDrafts: () => [...paymentKeys.all, "email-drafts"] as const,
  invoicesPdf: () => [...paymentKeys.all, "invoices-pdf"] as const,
  followUps: (externalId: string) =>
    [...paymentKeys.detail(externalId), "follow-ups"] as const,
  notes: (externalId: string) =>
    [...paymentKeys.detail(externalId), "notes"] as const,
};

export const organizationKeys = {
  all: ["organization"] as const,
  detail: (orgId: number | null | undefined) =>
    [...organizationKeys.all, orgId ?? "unknown"] as const,
  refreshStatus: ["organization", "refresh-status"] as const,
  outgoingMailbox: ["organization", "outgoing-mailbox"] as const,
};

export const customerRepresentativeKeys = {
  all: ["customer-representatives"] as const,
  list: (search?: string) =>
    search
      ? ([...customerRepresentativeKeys.all, "list", "search", search] as const)
      : ([...customerRepresentativeKeys.all, "list"] as const),
  detail: (id: number) =>
    [...customerRepresentativeKeys.all, "detail", id] as const,
  byPayment: (paymentExternalId: string) =>
    [
      ...customerRepresentativeKeys.all,
      "by-payment",
      paymentExternalId,
    ] as const,
  notes: (id: number) =>
    [...customerRepresentativeKeys.detail(id), "notes"] as const,
  blocked: () => [...customerRepresentativeKeys.all, "blocked"] as const,
};

export const userKeys = {
  info: ["user", "info"] as const,
  emailSignature: ["user", "email-signature"] as const,
  mailboxSyncStatus: ["user", "mailbox-sync-status"] as const,
};

export const adminKeys = {
  organizations: ["admin", "organizations"] as const,
  users: ["admin", "users"] as const,
  draftValidation: ["admin", "draft-validation"] as const,
};

export const notificationKeys = {
  all: ["notifications"] as const,
  list: () => [...notificationKeys.all, "list"] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
};

export const processingTaskKeys = {
  all: ["processing-tasks"] as const,
  task: (jobId: string) => [...processingTaskKeys.all, jobId] as const,
  inflight: (taskType: string) =>
    [...processingTaskKeys.all, "inflight", taskType] as const,
};
